import { z } from "zod";
import { DirectoryListingResponse, FileContentResponse, SnapshotDiffScope } from "./api.ts";
import { ChatEventBase, OrgRole, SnapshotSummary, requireTurnAttribution } from "./models.ts";
import { PreviewServiceRegistration } from "./preview-protocol.ts";
import { JOB_EVENT_BATCH_MAX } from "./protocol.ts";
import { DIRECT_TICKET_TTL_MS, isLoopbackAudience } from "./transport-descriptor.ts";

/**
 * LocalApi: a same-machine data plane the daemon serves on its loopback
 * gateway (`LoopbackTransportDescriptor.localApi`). It is an optimisation
 * only. Cloudflare stays the authority for ordering, durability and fan-out;
 * everything LocalApi shows is either read straight off the worker's disk or
 * provisional until the relay confirms it, and every client falls back to the
 * relay on any error.
 *
 * Authority comes from the API, never from being on localhost: the client
 * mints a one-use ticket through the relay, the daemon redeems it over its own
 * authenticated worker path for a `LocalSessionGrant`, and answers with an
 * `rvl_` bearer token valid for `LOCAL_SESSION_TTL_MS`.
 */

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/** Lifetime of an `rvl_` session token. A revoked grant can keep reading for
 * at most this long when the `local.revoke` push is lost. */
export const LOCAL_SESSION_TTL_MS = 15 * 60_000;
/** Lifetime of the one-use `rvd_` ticket the API mints for a session. */
export const LOCAL_SESSION_TICKET_TTL_MS = DIRECT_TICKET_TTL_MS;
/** Clients renew in the background once this fraction of the TTL has passed. */
export const LOCAL_SESSION_RENEW_FRACTION = 0.8;
/** LocalApi sockets, separate from the terminal's `DIRECT_MAX_CONNECTIONS`. */
export const LOCAL_API_MAX_CONNECTIONS = 32;
/** Per-token ceiling on HTTP requests plus socket frames. */
export const LOCAL_API_REQUESTS_PER_SECOND = 300;
/** Global ceiling on failed authentications. Past it every unauthenticated
 * request gets 429 (sockets close 1013) for `LOCAL_API_FAILED_AUTH_LOCKOUT_MS`. */
export const LOCAL_API_FAILED_AUTH_PER_SECOND = 20;
export const LOCAL_API_FAILED_AUTH_LOCKOUT_MS = 10_000;
/** WebSocket close code for capacity and rate limits ("try again later"). */
export const LOCAL_API_OVERLOAD_CLOSE_CODE = 1013;
/** A client that cannot reach LocalApi within this budget uses the relay, and
 * does not try LocalApi again for the rest of its session. */
export const LOCAL_API_CONNECT_BUDGET_MS = 300;
/** A provisional event the relay has not confirmed within this long is dropped. */
export const LOCAL_PROVISIONAL_TTL_MS = 60_000;

/** CORS preflight answers for an allowed web origin. */
export const LOCAL_API_CORS_HEADERS = {
  "Access-Control-Allow-Private-Network": "true",
  "Access-Control-Max-Age": "7200",
  "Access-Control-Allow-Methods": "GET, POST",
  "Access-Control-Allow-Headers": "authorization, content-type",
  Vary: "Origin",
} as const;

/** When a client should renew a session that expires at `expiresAt`: 80% of
 * the TTL after issue, never in the past. */
export function localSessionRenewDelayMs(expiresAt: number, now: number): number {
  // Rounded: `1 - 0.8` is 0.19999999999999996 in binary floating point.
  const renewAt = expiresAt - Math.round(LOCAL_SESSION_TTL_MS * (1 - LOCAL_SESSION_RENEW_FRACTION));
  return Math.max(0, renewAt - now);
}

// ---------------------------------------------------------------------------
// Tickets and grants (API)
// ---------------------------------------------------------------------------

const Id = z.string().min(1).max(160).regex(/^[A-Za-z0-9._:-]+$/);
const WorkerThumbprint = z.string().min(32).max(128).regex(/^[A-Za-z0-9_-]+$/);
const LoopbackAudience = z.string().url().max(2_048).refine(isLoopbackAudience, "audience must be the worker's loopback endpoint");

/** The one-use ticket a client exchanges for a session (60 s, stored hashed). */
export const LocalSessionTicket = z.string().min(40).max(256).regex(/^rvd_[A-Za-z0-9_-]+$/);
/** The daemon-issued session bearer: `rvl_` + 32 random bytes, base64url. */
export const LocalSessionToken = z.string().regex(/^rvl_[A-Za-z0-9_-]{43}$/);

/** POST /api/direct/local-session. The audience is the worker's loopback
 * audience (`http://127.0.0.1:<port>/v1/direct`). */
export const LocalSessionTicketRequest = z.object({
  workerId: Id,
  audience: LoopbackAudience,
});
export type LocalSessionTicketRequest = z.infer<typeof LocalSessionTicketRequest>;

export const LocalSessionTicketResponse = z.object({
  ticket: LocalSessionTicket,
  expiresAt: z.number().int().positive(),
  audience: z.string().url(),
  workerThumbprint: WorkerThumbprint,
});
export type LocalSessionTicketResponse = z.infer<typeof LocalSessionTicketResponse>;

/** POST /api/direct/local-session/consume, daemon → API over the worker's
 * proof-of-possession path. Answers `LocalSessionGrant`. */
export const LocalSessionConsumeRequest = z.object({
  ticket: LocalSessionTicket,
  audience: LoopbackAudience,
});
export type LocalSessionConsumeRequest = z.infer<typeof LocalSessionConsumeRequest>;

/** What a redeemed ticket authorises. The daemon holds it in memory only,
 * keyed by the session token. Timestamps are Unix ms and
 * `expiresAt = issuedAt + LOCAL_SESSION_TTL_MS`. */
export const LocalSessionGrant = z.object({
  grantId: Id,
  workerId: Id,
  orgId: Id,
  userId: z.string().min(1).max(160),
  role: OrgRole,
  /** The `requireOrgWrite*` predicate: may this user write (cancel) at all. */
  canWrite: z.boolean(),
  /** `org.status = 'active'`. A suspended org reads but never writes. */
  orgActive: z.boolean(),
  /** Worktrees on this worker, not archived, the user may see
   * (`repoAccessPredicate` plus the assistant-workspace creator rule). */
  worktreeIds: z.array(z.string().min(1).max(160)).max(10_000),
  issuedAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
});
export type LocalSessionGrant = z.infer<typeof LocalSessionGrant>;

/** The part of a grant the daemon echoes to the client. */
export const LocalGrantSummary = LocalSessionGrant.pick({
  grantId: true,
  userId: true,
  orgId: true,
  canWrite: true,
  orgActive: true,
  worktreeIds: true,
});
export type LocalGrantSummary = z.infer<typeof LocalGrantSummary>;

// ---------------------------------------------------------------------------
// Provisional chat events
// ---------------------------------------------------------------------------

/**
 * A chat event the daemon has spooled but the relay has not yet sequenced. It
 * has no `seq`, and its `eventId` is the exact id the relay will store
 * (`agentDeliveryEventId(workerId, deliveryId, deliverySeq, index)`), which is
 * how a client promotes it when the confirmed event arrives.
 *
 * Clients keep provisionals in a separate list keyed by `eventId`, fold them
 * as a suffix over a copy of the confirmed fold state (block ids
 * `provisionalBlockId(eventId)`), never persist them, and drop them on a
 * confirmed terminal `job.status` for their job, after
 * `LOCAL_PROVISIONAL_TTL_MS`, on local disconnect, and on unsubscribe.
 */
export const ProvisionalChatEvent = ChatEventBase.omit({ seq: true, eventId: true }).extend({
  eventId: z.string().min(1).max(300),
  provisional: z.literal(true),
}).superRefine(requireTurnAttribution);
export type ProvisionalChatEvent = z.infer<typeof ProvisionalChatEvent>;

/** The fold block id of a provisional event. */
export function provisionalBlockId(eventId: string): string {
  return `p-${eventId}`;
}

// ---------------------------------------------------------------------------
// Session socket: WS `${basePath}/ws`, JSON text frames
// ---------------------------------------------------------------------------

export const LocalClientFrame = z.discriminatedUnion("type", [
  /** First frame on a socket: a fresh `ticket`, or the `token` of a session
   * this client already holds (reconnect). Exactly one of the two. */
  z.object({
    type: z.literal("local.hello"),
    ticket: LocalSessionTicket.optional(),
    token: LocalSessionToken.optional(),
    /** LocalApi version the client speaks; absent means 1. */
    version: z.number().int().positive().optional(),
  }).refine((hello) => (hello.ticket === undefined) !== (hello.token === undefined), "local.hello needs exactly one of ticket or token"),
  /** Swap a freshly minted ticket for a new token on the open socket. */
  z.object({ type: z.literal("local.renew"), ticket: LocalSessionTicket }),
  z.object({ type: z.literal("watch.worktree"), worktreeId: z.string().min(1).max(160) }),
  z.object({ type: z.literal("unwatch.worktree"), worktreeId: z.string().min(1).max(160) }),
  /** Provisional tail for one chat of a granted worktree. */
  z.object({ type: z.literal("subscribe.chat"), chatId: z.string().min(1).max(160) }),
  z.object({ type: z.literal("unsubscribe.chat"), chatId: z.string().min(1).max(160) }),
  z.object({ type: z.literal("ping"), t: z.number() }),
]);
export type LocalClientFrame = z.infer<typeof LocalClientFrame>;

export const LocalSessionRejectCode = z.enum([
  "invalid_ticket", "expired", "audience_mismatch", "capacity", "rate_limited", "version",
]);
export type LocalSessionRejectCode = z.infer<typeof LocalSessionRejectCode>;

export const LocalServerFrame = z.discriminatedUnion("type", [
  z.discriminatedUnion("accepted", [
    z.object({
      type: z.literal("local.hello.result"),
      accepted: z.literal(true),
      token: LocalSessionToken,
      expiresAt: z.number().int().positive(),
      grant: LocalGrantSummary,
    }),
    z.object({
      type: z.literal("local.hello.result"),
      accepted: z.literal(false),
      code: LocalSessionRejectCode,
    }),
  ]),
  /** Answer to `local.renew`. On success the old token stays valid until its
   * own expiry; on failure the socket stays on the old token until then. */
  z.discriminatedUnion("accepted", [
    z.object({
      type: z.literal("local.renew.result"),
      accepted: z.literal(true),
      token: LocalSessionToken,
      expiresAt: z.number().int().positive(),
      grant: LocalGrantSummary,
    }),
    z.object({
      type: z.literal("local.renew.result"),
      accepted: z.literal(false),
      code: LocalSessionRejectCode,
    }),
  ]),
  /** A watched worktree changed on disk; replaces snapshot refetches. */
  z.object({
    type: z.literal("worktree.changed"),
    worktreeId: z.string().min(1).max(160),
    summary: SnapshotSummary,
  }),
  /** Agent output the daemon has durably spooled for the relay, sent after
   * the enqueue of that exact (redacted) frame succeeded. */
  z.object({
    type: z.literal("chat.provisional"),
    chatId: z.string().min(1).max(160),
    deliveryId: z.string().uuid(),
    deliverySeq: z.number().int().positive(),
    events: z.array(ProvisionalChatEvent).min(1).max(JOB_EVENT_BATCH_MAX),
  }),
  /** The grant behind this socket was revoked; the socket closes next. */
  z.object({ type: z.literal("local.revoked"), code: z.string().min(1).max(80) }),
  z.object({ type: z.literal("pong"), t: z.number() }),
]);
export type LocalServerFrame = z.infer<typeof LocalServerFrame>;

// ---------------------------------------------------------------------------
// HTTP: `Authorization: Bearer rvl_…` on every request
// ---------------------------------------------------------------------------

/** Every LocalApi error body. */
export const LocalApiError = z.object({
  code: z.string().min(1).max(80),
  message: z.string().max(1_000),
});
export type LocalApiError = z.infer<typeof LocalApiError>;

export const LocalApiErrorCode = {
  /** 401: missing, unknown or expired token. */
  unauthorized: "unauthorized",
  /** 403: the grant does not cover this worktree. Re-mint once, then relay. */
  worktreeNotGranted: "worktree_not_granted",
  /** 403: `Host` or `Origin` failed the loopback hardening checks. */
  forbidden: "forbidden",
  /** 400: bad query or body, or an unsafe path. */
  invalidRequest: "invalid_request",
  /** 404 */
  notFound: "not_found",
  /** 410: `snapshotId` is not the worktree's current local snapshot. */
  snapshotGone: "snapshot_gone",
  /** 429: per-token or global failed-auth limit. */
  rateLimited: "rate_limited",
} as const;

/** Local snapshot ids are `lsnap_<hash>` and only meaningful to the daemon
 * that issued them; they never reach the relay. */
export const LOCAL_SNAPSHOT_ID_PREFIX = "lsnap_";
export function isLocalSnapshotId(value: string): boolean {
  return value.startsWith(LOCAL_SNAPSHOT_ID_PREFIX) && value.length > LOCAL_SNAPSHOT_ID_PREFIX.length;
}

/** GET …/worktrees/:id/dir?path= — the relay shape, read live off disk. */
export const LocalDirectoryResponse = DirectoryListingResponse;
export type LocalDirectoryResponse = DirectoryListingResponse;
/** GET …/worktrees/:id/file?path= — the relay shape, same caps and safe-path
 * resolution as the relay file fetch. */
export const LocalFileResponse = FileContentResponse;
export type LocalFileResponse = FileContentResponse;
/** GET …/worktrees/:id/snapshot answers `SnapshotSummary` with an `lsnap_`
 * id, memoised until the fs watch sees a change. */
export const LocalSnapshotResponse = SnapshotSummary;
export type LocalSnapshotResponse = SnapshotSummary;

/** GET …/worktrees/:id/diff — answers `SnapshotDiffResponse`, or 410
 * `snapshot_gone` when `snapshotId` is no longer current. */
export const LocalDiffQuery = z.object({
  snapshotId: z.string().refine(isLocalSnapshotId, "snapshotId must be an lsnap_ id"),
  path: z.string().min(1).max(4_096),
  scope: SnapshotDiffScope,
});
export type LocalDiffQuery = z.infer<typeof LocalDiffQuery>;

/** GET …/worktrees/:id/previews — ports included; never crosses Cloudflare. */
export const LocalPreviewsResponse = z.object({ services: z.array(PreviewServiceRegistration) });
export type LocalPreviewsResponse = z.infer<typeof LocalPreviewsResponse>;

/** POST …/chats/:chatId/cancel — a fast-path hint. The daemon cancels `jobId`
 * when that job is active in its JobManager and the grant can write to its
 * worktree; it records nothing new, and the usual `job.update canceled` flows
 * through the spool. The client always sends the relay cancel for the same
 * `jobId` as well. */
export const LocalCancelRequest = z.object({ jobId: z.string().min(1).max(160) });
export type LocalCancelRequest = z.infer<typeof LocalCancelRequest>;
export const LocalCancelResponse = z.object({
  accepted: z.boolean(),
  /** Why not, when `accepted` is false: e.g. `not_active`, `read_only`. */
  code: z.string().min(1).max(80).optional(),
});
export type LocalCancelResponse = z.infer<typeof LocalCancelResponse>;

/** Paths under the advertised base path (`LOCAL_API_BASE_PATH`). */
export const localApiRoutes = {
  ws: "/ws",
  dir: (worktreeId: string, path: string) => `/worktrees/${encodeURIComponent(worktreeId)}/dir?path=${encodeURIComponent(path)}`,
  file: (worktreeId: string, path: string) => `/worktrees/${encodeURIComponent(worktreeId)}/file?path=${encodeURIComponent(path)}`,
  snapshot: (worktreeId: string) => `/worktrees/${encodeURIComponent(worktreeId)}/snapshot`,
  diff: (worktreeId: string, query: LocalDiffQuery) =>
    `/worktrees/${encodeURIComponent(worktreeId)}/diff?snapshotId=${encodeURIComponent(query.snapshotId)}&path=${encodeURIComponent(query.path)}&scope=${query.scope}`,
  previews: (worktreeId: string) => `/worktrees/${encodeURIComponent(worktreeId)}/previews`,
  cancel: (chatId: string) => `/chats/${encodeURIComponent(chatId)}/cancel`,
} as const;
